import type { CallInfo } from '@compass/client/types/call';
import type { Evaluation } from '@compass/client/types/evaluation';
import type { FeedbackInfo } from '@compass/client/types/feedback';
import type { Scorecard } from '@compass/client/types/scorecard';
import type { Summary } from '@compass/client/types/summary';
import React from 'react';
import { Link } from 'react-router-dom';

import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Badge } from '@/components/ui/badge';
import { CopyButton } from '@/components/ui/copy-button';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { useShouldShowAnalyticsPage } from '@/hooks/useShouldShowAnalyticsPage';
import { buildAnalyticsAgentDetailPath } from '@/pages/analytics/utils/resolveOrg';
import {
  formatAggregateScore,
  formatIneligibilityReasonTitle,
  getIneligibilityReason,
} from '@/pages/qc/utils';
import { cn } from '@/utils';
import { buildRoutePath } from '@/utils/routes';

import {
  formatDurationFromCall,
  formatMetadataValue,
  getCallDateTimeValue,
  getEvaluationDirectionLabel,
  getExtractedDetailFields,
  getMetadataReasonValue,
  getQcScoreLabel,
  getQcScoreToneClasses,
  getReviewOutcomeMetadataFields,
  getSummaryFieldArrayValue,
  getSummaryFieldValue,
} from '../qcDetail.utils';
import { QcFeedbackPopover } from './QcFeedbackPopover';
import { QcReviewAlerts } from './QcReviewAlerts';
import { getEvaluationWorkflowBannerState, QcWorkflowStatusBanner } from './QcWorkflowStatusBanner';

const EMPTY_VALUE = '--';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="border-b px-4 py-2">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
    </h3>
  </div>
);

const DetailGrid: React.FC<{ children: React.ReactNode; columns?: 3 | 4 }> = ({
  children,
  columns = 4,
}) => (
  <div
    className={cn(
      'grid gap-4 px-4 py-4',
      columns === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 xl:grid-cols-4',
    )}
  >
    {React.Children.toArray(children).map((child, index) => (
      <div key={index} className="min-w-0">
        {child}
      </div>
    ))}
  </div>
);

const DetailField: React.FC<{
  label: string;
  tooltip?: string | null;
  children: React.ReactNode;
}> = ({ label, tooltip, children }) => (
  <div className="space-y-1">
    <div className="flex">
      <SimpleTooltipV2
        content={
          tooltip ? (
            <span className="flex max-w-xs flex-col gap-2 whitespace-normal break-words text-left leading-relaxed">
              {tooltip}
            </span>
          ) : undefined
        }
        side="bottom"
      >
        <p className={cn('text-xs text-muted-foreground', tooltip && 'cursor-help')}>{label}</p>
      </SimpleTooltipV2>
    </div>
    <div className="min-w-0 whitespace-normal break-words text-sm font-medium text-foreground [overflow-wrap:anywhere]">
      {children}
    </div>
  </div>
);

const METADATA_BADGE_TONE_CLASSES = {
  warning: 'border-transparent bg-attention text-attention-foreground',
  danger: 'border-transparent bg-critical text-critical-foreground',
} as const;

const BOOLEAN_DANGER_FIELDS = new Set(['escalation_required', 'compliance_failed']);

const getReviewOutcomeBadgeProps = (fieldKey: string, value: string) => {
  const normalizedValue = value.trim().toLowerCase();

  if (BOOLEAN_DANGER_FIELDS.has(fieldKey)) {
    if (normalizedValue === 'true') {
      return { label: 'True', className: METADATA_BADGE_TONE_CLASSES.danger };
    }
    if (normalizedValue === 'false') {
      return { label: 'False', className: 'border-transparent bg-muted text-foreground' };
    }
  }

  return { label: value, className: '' };
};

const ReviewOutcomeBadge: React.FC<{
  fieldKey: string;
  value: string;
}> = ({ fieldKey, value }) => {
  if (value === 'N/A') {
    return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
  }

  const badge = getReviewOutcomeBadgeProps(fieldKey, value);

  return (
    <Badge
      variant="outline"
      className={cn(
        'max-w-full whitespace-normal break-words text-xs [overflow-wrap:anywhere]',
        badge.className,
      )}
    >
      {badge.label}
    </Badge>
  );
};

const ChipGroup: React.FC<{ values: string[] }> = ({ values }) => {
  if (values.length === 0) {
    return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map(value => (
        <Badge
          key={value}
          variant="secondary"
          className="max-w-full whitespace-normal break-words text-xs [overflow-wrap:anywhere]"
        >
          {value}
        </Badge>
      ))}
    </div>
  );
};

const CallIdValue: React.FC<{ evaluation: Evaluation; showCaseBacklink?: boolean }> = ({
  evaluation,
  showCaseBacklink,
}) => {
  if (!evaluation.call_id) return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;

  const canLink =
    showCaseBacklink &&
    evaluation.case_record_id &&
    evaluation.organization_id &&
    evaluation.program_id;

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {canLink ? (
        <Link
          to={`${buildRoutePath(
            `/cases/${evaluation.case_record_id}`,
            evaluation.organization_id!,
            evaluation.program_id!,
          )}?callId=${evaluation.call_id}`}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 break-all font-mono text-xs text-primary underline-offset-4 hover:underline"
        >
          {evaluation.call_id}
        </Link>
      ) : (
        <span className="min-w-0 break-all font-mono text-xs">{evaluation.call_id}</span>
      )}
      <CopyButton value={evaluation.call_id} label="Call ID" className="shrink-0" />
    </div>
  );
};

const AgentNameValue: React.FC<{ evaluation: Evaluation; agentName?: string | null }> = ({
  evaluation,
  agentName,
}) => {
  // Inner component holds the analytics hook so it's skipped when no name.
  if (!agentName) return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;

  return <AgentNameLink evaluation={evaluation} agentName={agentName} />;
};

const AgentNameLink: React.FC<{ evaluation: Evaluation; agentName: string }> = ({
  evaluation,
  agentName,
}) => {
  // Only link when the user can reach the org's Analytics page.
  const { shouldShow: canViewAnalytics, isLoading } = useShouldShowAnalyticsPage({
    organizationId: evaluation.organization_id ?? null,
  });

  const orgId = evaluation.organization_id;
  const agentId = evaluation.agent_user_id;

  // Plain text while access is resolving (avoids a text→link flash), when the
  // user lacks analytics access, or when the agent/org can't be resolved.
  if (isLoading || !canViewAnalytics || !orgId || !agentId) {
    return <span>{agentName}</span>;
  }

  // No filter params: the Agent Detail page applies its own defaults.
  const agentAnalyticsPath = `${buildAnalyticsAgentDetailPath(orgId, agentId)}?tab=agents`;

  return (
    <Link
      to={agentAnalyticsPath}
      title="View agent analytics"
      className="text-primary underline-offset-4 hover:underline"
    >
      {agentName}
    </Link>
  );
};

interface QcDetailSummaryTabProps {
  evaluation: Evaluation;
  scorecard?: Scorecard | null;
  call?: CallInfo | null;
  callSummary?: Summary | null;
  evaluationFeedbacks?: FeedbackInfo[];
  agentName?: string | null;
  programName?: string | null;
  skillName?: string | null;
  campaignName?: string | null;
  teamName?: string | null;
  queueName?: string | null;
  userNameById?: Map<string, string>;
  showCaseBacklink?: boolean;
}

export const QcDetailSummaryTab: React.FC<QcDetailSummaryTabProps> = ({
  evaluation,
  scorecard,
  call,
  callSummary,
  evaluationFeedbacks,
  agentName,
  programName,
  skillName,
  campaignName,
  teamName,
  queueName,
  userNameById,
  showCaseBacklink,
}) => {
  const { shouldShowWorkflowBanner } = getEvaluationWorkflowBannerState(evaluation);
  const summaryText = callSummary?.content?.full_summary?.trim() || null;
  const duration = formatDurationFromCall(call);
  const direction = getEvaluationDirectionLabel(evaluation.call_direction);
  const callDateTime = getCallDateTimeValue(call);
  // CXOne calls carry contact_id; Genesys calls carry conversation_id instead
  // (no contact_id). Fall back so the source identifier renders for both.
  const sourceIdentifier = call?.input_data?.contact_id || call?.input_data?.conversation_id;
  const extractedDetailFields = getExtractedDetailFields(scorecard, evaluation);
  const reviewOutcomeMetadataFields = getReviewOutcomeMetadataFields(scorecard);
  const scoreLabel = getQcScoreLabel(evaluation.aggregate_score);
  // AQ-653: ineligible calls have no score. Surface the ineligible status and its
  // reason in the score chip instead of a bare "--".
  const isIneligible = !!evaluation.ineligible;
  const ineligibilityReasonTitle = isIneligible
    ? formatIneligibilityReasonTitle(getIneligibilityReason(evaluation))
    : null;

  return (
    <div className="space-y-3 px-4 py-4">
      <QcWorkflowStatusBanner evaluation={evaluation} className="mb-2" />

      {/* Score */}
      {(!shouldShowWorkflowBanner || summaryText) && (
        <div className="rounded-md border bg-background">
          {!shouldShowWorkflowBanner && (
            <>
              <div className="flex items-center gap-3 px-4 py-3">
                {isIneligible ? (
                  <div className="flex flex-col items-start gap-1.5">
                    <Badge variant="secondary" className="text-muted-foreground">
                      Rating Ineligible
                    </Badge>
                    {ineligibilityReasonTitle && (
                      <span className="text-sm text-muted-foreground">
                        Reason: {ineligibilityReasonTitle}
                      </span>
                    )}
                  </div>
                ) : (
                  <>
                    <span
                      className={`text-2xl font-bold ${getQcScoreToneClasses(evaluation.aggregate_score)}`}
                    >
                      {evaluation.aggregate_score === undefined ||
                      evaluation.aggregate_score === null
                        ? EMPTY_VALUE
                        : formatAggregateScore(evaluation.aggregate_score)}
                    </span>
                    <Badge variant="secondary" className={scoreLabel.toneClass}>
                      {scoreLabel.content}
                    </Badge>
                  </>
                )}
                <div className="ml-auto self-start">
                  {evaluation.id && (
                    <QcFeedbackPopover
                      evaluationId={evaluation.id}
                      feedback={evaluationFeedbacks}
                      userNameById={userNameById}
                    />
                  )}
                </div>
              </div>

              <QcReviewAlerts evaluation={evaluation} />
            </>
          )}

          {summaryText && (
            <>
              <SectionHeader title="Call Summary" />
              <div className="px-4 py-3 text-sm text-muted-foreground">{summaryText}</div>
            </>
          )}
        </div>
      )}

      {/* Call details + extracted + review outcomes */}
      <div className="rounded-md border bg-background">
        <SectionHeader title="Call Details" />
        <DetailGrid>
          <DetailField label="Agent Name">
            <AgentNameValue evaluation={evaluation} agentName={agentName} />
          </DetailField>
          <DetailField label="Program">{programName || EMPTY_VALUE}</DetailField>
          <DetailField label="Call ID">
            <CallIdValue evaluation={evaluation} showCaseBacklink={showCaseBacklink} />
          </DetailField>
          <DetailField label="Source Identifier">
            {sourceIdentifier ? (
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="min-w-0 break-all font-mono text-xs">
                  {String(sourceIdentifier)}
                </span>
                <CopyButton
                  value={String(sourceIdentifier)}
                  label="Source Identifier"
                  className="shrink-0"
                />
              </div>
            ) : (
              <span className="text-muted-foreground">{EMPTY_VALUE}</span>
            )}
          </DetailField>
          <DetailField label="Call Date-Time">
            {callDateTime ? <FormattedDateRelative value={callDateTime} /> : EMPTY_VALUE}
          </DetailField>
          <DetailField label="Duration">{duration || EMPTY_VALUE}</DetailField>
          <DetailField label="Call Direction">{direction || EMPTY_VALUE}</DetailField>
        </DetailGrid>

        {(skillName || campaignName || teamName || queueName) && (
          <div className="border-t">
            <SectionHeader title="Source Details" />
            <DetailGrid>
              {skillName && <DetailField label="Skill Name">{skillName}</DetailField>}
              {campaignName && <DetailField label="Campaign Name">{campaignName}</DetailField>}
              {teamName && <DetailField label="Team Name">{teamName}</DetailField>}
              {queueName && <DetailField label="Queue Name">{queueName}</DetailField>}
            </DetailGrid>
          </div>
        )}

        {extractedDetailFields.length > 0 && (
          <div className="border-t">
            <SectionHeader title="Extracted Details" />
            <DetailGrid>
              {extractedDetailFields.map(field => (
                <DetailField
                  key={field.key}
                  label={field.label}
                  tooltip={getMetadataReasonValue(evaluation, field.key)}
                >
                  <ChipGroup
                    values={getSummaryFieldArrayValue(evaluation, [field.key]).map(
                      formatMetadataValue,
                    )}
                  />
                </DetailField>
              ))}
            </DetailGrid>
          </div>
        )}

        <div className="border-t">
          <SectionHeader title="Review Outcomes" />
          <DetailGrid>
            <DetailField
              label="Escalation Required"
              tooltip={evaluation.reasons?.escalation || null}
            >
              <ReviewOutcomeBadge
                fieldKey="escalation_required"
                value={
                  evaluation.flags?.escalation === true
                    ? 'True'
                    : evaluation.flags?.escalation === false
                      ? 'False'
                      : 'N/A'
                }
              />
            </DetailField>
            <DetailField
              label="Compliance Failed"
              tooltip={evaluation.reasons?.compliance_failed || null}
            >
              <ReviewOutcomeBadge
                fieldKey="compliance_failed"
                value={
                  evaluation.flags?.compliance_failed === true
                    ? 'True'
                    : evaluation.flags?.compliance_failed === false
                      ? 'False'
                      : 'N/A'
                }
              />
            </DetailField>
            {reviewOutcomeMetadataFields.map(field => (
              <DetailField
                key={field.key}
                label={field.label}
                tooltip={getMetadataReasonValue(evaluation, field.key)}
              >
                <ReviewOutcomeBadge
                  fieldKey={field.key}
                  value={getSummaryFieldValue(evaluation, [field.key]) || 'N/A'}
                />
              </DetailField>
            ))}
          </DetailGrid>
        </div>
      </div>
    </div>
  );
};
